import React, { useEffect } from 'react';
import { ArrowRight, Droplets, Thermometer, Wrench, AlertTriangle, ShieldCheck, ClipboardList, HelpCircle, CheckCircle2, XCircle, Info, MessageCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import Navbar from '@/components/Navbar';
import Footer from '@/components/Footer';
import GlobalBanner from '@/components/GlobalBanner';
import RelatedArticles from '@/components/RelatedArticles';

/* ─── Inline SVG: Split System Diagram ─── */
const SplitSystemDiagram = () => (
  <div data-testid="split-system-diagram" className="w-full max-w-2xl mx-auto py-6">
    <svg viewBox="0 0 700 260" className="w-full" aria-label="Split HVAC system diagram showing outdoor condenser connected to indoor evaporator via refrigerant lines">
      {/* Outdoor Unit */}
      <rect x="30" y="60" width="200" height="160" rx="12" fill="#F1F5F9" stroke="#CBD5E1" strokeWidth="2" />
      <rect x="60" y="90" width="140" height="80" rx="8" fill="#E2E8F0" stroke="#94A3B8" strokeWidth="1.5" />
      {/* Fan blades */}
      <circle cx="130" cy="130" r="28" fill="none" stroke="#64748B" strokeWidth="1.5" />
      <line x1="130" y1="102" x2="130" y2="158" stroke="#64748B" strokeWidth="1.5" />
      <line x1="102" y1="130" x2="158" y2="130" stroke="#64748B" strokeWidth="1.5" />
      <line x1="110" y1="110" x2="150" y2="150" stroke="#64748B" strokeWidth="1.5" />
      <line x1="150" y1="110" x2="110" y2="150" stroke="#64748B" strokeWidth="1.5" />
      <text x="130" y="195" textAnchor="middle" fill="#0F172A" fontSize="13" fontWeight="600" fontFamily="Outfit, sans-serif">Outdoor Condenser</text>
      <text x="130" y="45" textAnchor="middle" fill="#64748B" fontSize="11" fontFamily="sans-serif">OUTSIDE YOUR HOME</text>

      {/* Refrigerant Lines */}
      <line x1="230" y1="120" x2="470" y2="120" stroke="#0EA5E9" strokeWidth="3" strokeDasharray="8 4" />
      <line x1="230" y1="150" x2="470" y2="150" stroke="#F97316" strokeWidth="3" strokeDasharray="8 4" />
      <text x="350" y="108" textAnchor="middle" fill="#0EA5E9" fontSize="10" fontWeight="600" fontFamily="sans-serif">LIQUID LINE (cool)</text>
      <text x="350" y="175" textAnchor="middle" fill="#F97316" fontSize="10" fontWeight="600" fontFamily="sans-serif">SUCTION LINE (warm)</text>
      {/* Arrow heads */}
      <polygon points="460,115 470,120 460,125" fill="#0EA5E9" />
      <polygon points="240,145 230,150 240,155" fill="#F97316" />

      {/* Indoor Unit */}
      <rect x="470" y="60" width="200" height="160" rx="12" fill="#F1F5F9" stroke="#CBD5E1" strokeWidth="2" />
      <rect x="500" y="90" width="140" height="80" rx="8" fill="#E2E8F0" stroke="#94A3B8" strokeWidth="1.5" />
      {/* Coil representation */}
      <path d="M520,105 Q530,105 530,115 Q530,125 540,125 Q550,125 550,135 Q550,145 560,145 Q570,145 570,155 Q570,160 620,160" fill="none" stroke="#0EA5E9" strokeWidth="2" />
      <path d="M520,110 Q530,110 530,120 Q530,130 540,130 Q550,130 550,140 Q550,150 560,150 Q570,150 570,155" fill="none" stroke="#0EA5E9" strokeWidth="1.5" opacity="0.5" />
      <text x="570" y="195" textAnchor="middle" fill="#0F172A" fontSize="13" fontWeight="600" fontFamily="Outfit, sans-serif">Indoor Evaporator</text>
      <text x="570" y="45" textAnchor="middle" fill="#64748B" fontSize="11" fontFamily="sans-serif">INSIDE YOUR HOME</text>

      {/* Same refrigerant label */}
      <rect x="270" y="215" width="160" height="30" rx="15" fill="#0F172A" />
      <text x="350" y="235" textAnchor="middle" fill="white" fontSize="11" fontWeight="600" fontFamily="sans-serif">MUST USE SAME REFRIGERANT</text>
    </svg>
  </div>
);

/* ─── Timeline Component ─── */
const TimelineItem = ({ period, name, subtitle, description, color, isLast }) => (
  <div className="flex-1 relative" data-testid={`timeline-item-${name.replace(/\s/g, '-').toLowerCase()}`}>
    {/* Connector line */}
    {!isLast && (
      <div className="hidden md:block absolute top-5 left-[calc(50%+20px)] right-0 h-0.5 bg-slate-200" />
    )}
    <div className="flex flex-col items-center text-center">
      <div className={`w-10 h-10 rounded-full ${color} flex items-center justify-center z-10 mb-3`}>
        <Droplets className="w-5 h-5 text-white" />
      </div>
      <span className="text-xs font-bold text-slate-400 tracking-wider uppercase mb-1">{period}</span>
      <h3 className="text-lg font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>{name}</h3>
      <p className="text-sm font-medium text-[#0EA5E9] mb-1">{subtitle}</p>
      <p className="text-sm text-slate-500 max-w-[200px]">{description}</p>
    </div>
  </div>
);

const REFRIGERANT_FAQS = [
  {
    question: 'Is R-410A being phased out?',
    answer: 'R-410A is not being banned or removed from existing systems. Starting in 2025, new HVAC equipment sold in the United States is transitioning to newer refrigerants with lower environmental impact. However, R-410A remains widely available for servicing, recharging, and repairing existing systems. Homeowners with R-410A systems do not need to replace their equipment because of this transition.',
  },
  {
    question: 'Can I still repair an R-22 air conditioner?',
    answer: 'Yes, in many cases R-22 systems can still be repaired. While R-22 production ended in 2020, reclaimed and recycled R-22 refrigerant is still available through licensed HVAC contractors. Minor repairs like replacing capacitors, contactors, or fan motors do not require refrigerant at all. However, if the system has a major refrigerant leak or needs a large recharge, the cost of R-22 may make replacement more practical depending on the system\'s age and condition.',
  },
  {
    question: 'What refrigerant replaces R-410A?',
    answer: 'The primary replacements for R-410A in new residential HVAC equipment are A2L refrigerants, including R-454B (also marketed as Puron Advance) and R-32. These refrigerants have a significantly lower global warming potential than R-410A. New systems manufactured from 2025 onward will use these newer refrigerants, but existing R-410A systems will continue to be supported and serviced for many years.',
  },
  {
    question: 'Can I replace only my outdoor unit?',
    answer: 'Sometimes, but not always. Both the outdoor condenser and indoor evaporator coil must use the same refrigerant type and be compatible with each other. If a matching indoor coil is available for the new outdoor unit, replacing just the condenser may be possible. However, mismatched components can reduce efficiency, void warranties, and cause premature equipment failure. A qualified HVAC technician can evaluate whether a partial replacement is practical for your specific system.',
  },
  {
    question: 'Will my R-410A system become obsolete?',
    answer: 'No. R-410A systems installed today will remain fully functional and serviceable for their entire expected lifespan. R-410A refrigerant will continue to be manufactured and available for servicing existing equipment. The transition to newer refrigerants applies only to newly manufactured HVAC equipment, not to systems already installed in homes.',
  },
  {
    question: 'How do I know what refrigerant my system uses?',
    answer: 'The refrigerant type is listed on the equipment data plate, which is usually a metal label attached to the side of your outdoor condenser unit. Look for a line that says "Refrigerant" or "Charge" followed by a designation like R-22, R-410A, R-454B, or R-32. If you cannot locate the data plate or read the label, any licensed HVAC technician can identify your refrigerant type during a routine service visit.',
  },
];

const RefrigerantGuidePage = () => {
  useEffect(() => {
    const script = document.createElement('script');
    script.type = 'application/ld+json';
    script.text = JSON.stringify({
      "@context": "https://schema.org",
      "@type": "FAQPage",
      "mainEntity": REFRIGERANT_FAQS.map(faq => ({
        "@type": "Question",
        "name": faq.question,
        "acceptedAnswer": {
          "@type": "Answer",
          "text": faq.answer,
        },
      })),
    });
    document.head.appendChild(script);
    return () => { document.head.removeChild(script); };
  }, []);
  return (
    <div className="min-h-screen bg-white">
      <GlobalBanner />
      <Navbar />

      {/* Hero */}
      <section className="pt-28 pb-12 bg-gradient-to-br from-slate-50 to-white" data-testid="refrigerant-guide-hero">
        <div className="max-w-4xl mx-auto px-6 text-center">
          <h1
            className="text-4xl md:text-5xl font-bold text-[#0F172A] mb-4"
            style={{ fontFamily: 'Outfit, sans-serif' }}
            data-testid="refrigerant-guide-heading"
          >
            HVAC Refrigerant Guide: R-22 vs R-410A vs New Refrigerants
          </h1>
          <p className="text-lg text-slate-600 max-w-2xl mx-auto mb-4">
            Air conditioners work by using <strong>refrigerant</strong> to transfer heat from inside
            your home to outside. Different generations of HVAC systems use different refrigerants,
            and understanding the differences helps you make smarter repair and replacement decisions.
          </p>
          <p className="text-base text-slate-500 max-w-2xl mx-auto mb-8">
            Refrigerant type alone does <strong>not</strong> automatically mean your system must be
            replaced. This guide explains what homeowners need to know.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button
              data-testid="hero-estimate-btn"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-8 rounded-full text-lg"
            >
              Get Your Instant HVAC Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* SECTION 1 — Refrigerant Timeline */}
      <section className="py-16" data-testid="refrigerant-timeline-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <Thermometer className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              HVAC Refrigerant Timeline
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-10">
            Residential HVAC refrigerants have evolved significantly over the past three decades.
          </p>

          {/* Timeline */}
          <div className="flex flex-col md:flex-row gap-8 md:gap-4">
            <TimelineItem
              period="1990 – 2010"
              name="R-22"
              subtitle='Often called "Freon"'
              description="Production ended in 2020. Supply is limited but existing systems can still be serviced."
              color="bg-slate-500"
            />
            <TimelineItem
              period="2010 – 2024"
              name="R-410A"
              subtitle="Most common modern refrigerant"
              description="Still used in millions of homes across the country. Widely available."
              color="bg-[#0EA5E9]"
            />
            <TimelineItem
              period="2025 & Beyond"
              name="A2L Refrigerants"
              subtitle="R-454B and R-32"
              description="Lower environmental impact refrigerants now used in newer equipment."
              color="bg-[#0F172A]"
              isLast
            />
          </div>
        </div>
      </section>

      {/* SECTION 2 — Why Components Must Match */}
      <section className="py-16 bg-slate-50" data-testid="components-match-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <Wrench className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Why HVAC Components Must Match
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-6">
            A typical residential HVAC system is a <strong>split system</strong> — meaning it has an
            outdoor condenser unit connected via refrigerant lines to an indoor evaporator coil or
            air handler.
          </p>

          <SplitSystemDiagram />

          <div className="bg-white rounded-2xl border border-slate-200 p-6 mt-6 space-y-3">
            <p className="text-slate-600 text-base">
              Both components must use the <strong>same refrigerant type</strong> to function correctly.
              The outdoor condenser, indoor coil, and refrigerant lines are all designed for a specific
              refrigerant pressure and chemical composition.
            </p>
            <p className="text-slate-600 text-base">
              This is why sometimes <strong>multiple components must be replaced together</strong> —
              not because of a sales tactic, but because mixing refrigerant-incompatible parts can
              cause equipment damage or system failure.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 3 — How to Identify Your Refrigerant */}
      <section className="py-16" data-testid="identify-refrigerant-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <ClipboardList className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How to Identify Your Refrigerant
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            You can usually determine your system's refrigerant type based on when it was installed.
          </p>

          {/* Reference Chart */}
          <div className="space-y-4" data-testid="refrigerant-chart">
            {[
              { era: 'Before 2010', refrigerant: 'R-22 (Freon)', status: 'Phased out — limited supply', color: 'border-slate-400', bg: 'bg-slate-50', dot: 'bg-slate-400' },
              { era: '2010 – 2024', refrigerant: 'R-410A (Puron)', status: 'Current standard — widely available', color: 'border-[#0EA5E9]', bg: 'bg-sky-50', dot: 'bg-[#0EA5E9]' },
              { era: '2025 or newer', refrigerant: 'A2L (R-454B / R-32)', status: 'Next generation — lower environmental impact', color: 'border-[#0F172A]', bg: 'bg-slate-100', dot: 'bg-[#0F172A]' },
            ].map((item) => (
              <div
                key={item.era}
                data-testid={`refrigerant-chart-row-${item.era.replace(/\s/g, '-')}`}
                className={`flex flex-col sm:flex-row sm:items-center gap-4 p-5 rounded-xl border-l-4 ${item.color} ${item.bg}`}
              >
                <div className="sm:w-40 flex-shrink-0">
                  <div className="flex items-center gap-2">
                    <span className={`w-2.5 h-2.5 rounded-full ${item.dot}`} />
                    <span className="font-bold text-[#0F172A] text-sm">{item.era}</span>
                  </div>
                </div>
                <div className="flex-1">
                  <p className="font-semibold text-[#0F172A]">{item.refrigerant}</p>
                  <p className="text-sm text-slate-500">{item.status}</p>
                </div>
              </div>
            ))}
          </div>

          <p className="text-slate-500 text-sm mt-6 italic">
            The exact refrigerant type can usually be confirmed on the equipment data plate located
            on the side of your outdoor condenser unit.
          </p>
        </div>
      </section>

      {/* SECTION 4 — Common Homeowner Scenario */}
      <section className="py-16 bg-slate-50" data-testid="homeowner-scenario-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <HelpCircle className="w-7 h-7 text-[#F97316]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              Common Homeowner Scenario
            </h2>
          </div>
          <div className="bg-white rounded-2xl border border-slate-200 p-8 mt-6">
            <p className="text-slate-600 text-base mb-4">
              A homeowner replaced their outdoor condenser in 2022 but kept the original indoor air
              handler. Both components currently use <strong>R-410A</strong> refrigerant, and the
              system functions normally.
            </p>
            <p className="text-slate-600 text-base mb-6">
              If the indoor evaporator coil fails later, possible options may include:
            </p>
            <div className="space-y-3">
              {[
                { text: 'Replacing just the indoor evaporator coil with a compatible R-410A unit', icon: CheckCircle2, color: 'text-green-600' },
                { text: 'Replacing the condenser and coil together if compatibility is uncertain', icon: AlertTriangle, color: 'text-yellow-600' },
                { text: 'Replacing the full system if age or condition makes individual repairs impractical', icon: Info, color: 'text-[#0EA5E9]' },
              ].map((item) => (
                <div key={item.text.slice(0, 15)} className="flex items-start gap-3">
                  <item.icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${item.color}`} />
                  <p className="text-slate-600 text-base">{item.text}</p>
                </div>
              ))}
            </div>
            <p className="text-slate-500 text-sm mt-6 italic">
              Manufacturers continue supporting existing refrigerant platforms for many years.
              An R-410A system installed today will remain serviceable well into the future.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 5 — When Replacement May Make Sense */}
      <section className="py-16" data-testid="when-replace-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <XCircle className="w-7 h-7 text-red-500" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              When Replacement May Actually Make Sense
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            Certain situations lead homeowners to consider full system replacement:
          </p>

          <div className="grid sm:grid-cols-2 gap-4">
            {[
              { title: 'Frequent Repair Needs', desc: 'Multiple breakdowns per season suggest the system is reaching end of life.', icon: Wrench },
              { title: 'Major Component Failures', desc: 'Compressor or evaporator coil failures can cost 50% or more of a new system.', icon: AlertTriangle },
              { title: 'Energy Efficiency Gains', desc: 'Newer systems can reduce cooling costs by 30–50% compared to older models.', icon: Thermometer },
              { title: 'R-22 Refrigerant Scarcity', desc: 'R-22 is no longer produced. Remaining supply is limited and increasingly expensive.', icon: Droplets },
            ].map((item) => (
              <div key={item.title} className="bg-slate-50 p-6 rounded-xl border border-slate-200">
                <div className="flex items-center gap-3 mb-3">
                  <div className="w-9 h-9 bg-red-50 rounded-lg flex items-center justify-center">
                    <item.icon className="w-5 h-5 text-red-500" />
                  </div>
                  <h3 className="font-bold text-[#0F172A] text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                </div>
                <p className="text-sm text-slate-500">{item.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* SECTION 6 — When Repair May Still Make Sense */}
      <section className="py-16 bg-slate-50" data-testid="when-repair-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <CheckCircle2 className="w-7 h-7 text-green-600" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              When Repair May Still Make Sense
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            Many HVAC repairs are relatively minor and do not require full system replacement.
            Common repairs include:
          </p>

          <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
            {['Capacitor', 'Contactor', 'Motor', 'Thermostat'].map((part) => (
              <div key={part} className="bg-white p-5 rounded-xl border border-slate-200 text-center">
                <Wrench className="w-6 h-6 text-green-600 mx-auto mb-2" />
                <p className="font-semibold text-[#0F172A] text-sm">{part} Replacement</p>
              </div>
            ))}
          </div>

          <p className="text-slate-500 text-sm mt-6">
            These types of repairs typically cost a fraction of a full system replacement and can
            extend the life of your existing equipment by several years.
          </p>
        </div>
      </section>

      {/* SECTION 7 — How Contractors Evaluate Repair vs Replacement */}
      <section className="py-16" data-testid="contractor-evaluation-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="flex items-center gap-3 mb-2">
            <ShieldCheck className="w-7 h-7 text-[#0EA5E9]" />
            <h2 className="text-3xl font-bold text-[#0F172A]" style={{ fontFamily: 'Outfit, sans-serif' }}>
              How Contractors Evaluate Repair vs Replacement
            </h2>
          </div>
          <p className="text-slate-600 text-lg mb-8">
            Professional HVAC technicians typically consider four primary factors when recommending
            repair or replacement:
          </p>

          <div className="space-y-4">
            {[
              { num: '1', title: 'System Age', desc: 'Older systems approaching end-of-life may not justify expensive repairs.' },
              { num: '2', title: 'Repair Cost Relative to System Value', desc: 'If a single repair costs 50% or more of a new system, replacement is often more practical.' },
              { num: '3', title: 'Refrigerant Compatibility', desc: 'Whether the existing refrigerant is still available and whether components can be matched.' },
              { num: '4', title: 'Overall Equipment Condition', desc: 'Ductwork integrity, electrical connections, and general wear across the entire system.' },
            ].map((item) => (
              <div key={item.num} className="flex items-start gap-4 bg-slate-50 p-5 rounded-xl border border-slate-200">
                <div className="w-9 h-9 bg-[#0EA5E9] rounded-full flex items-center justify-center flex-shrink-0">
                  <span className="text-white font-bold text-sm">{item.num}</span>
                </div>
                <div>
                  <h3 className="font-bold text-[#0F172A] text-base" style={{ fontFamily: 'Outfit, sans-serif' }}>{item.title}</h3>
                  <p className="text-sm text-slate-500 mt-1">{item.desc}</p>
                </div>
              </div>
            ))}
          </div>

          <div className="bg-sky-50 border border-sky-200 rounded-xl p-5 mt-8">
            <p className="text-slate-700 text-sm">
              <strong>Homeowner tip:</strong> Always feel comfortable asking your technician to explain
              the reasoning behind a repair or replacement recommendation. A trustworthy contractor
              will be happy to walk you through the factors and help you understand your options.
            </p>
          </div>
        </div>
      </section>

      {/* SECTION 8 — Refrigerant FAQ */}
      <section className="py-20 bg-[#F8FAFC]" data-testid="refrigerant-faq-section">
        <div className="max-w-4xl mx-auto px-6">
          <div className="text-center mb-12">
            <p className="text-sm font-medium tracking-wide uppercase text-[#0EA5E9] mb-4">FAQ</p>
            <h2
              className="text-3xl md:text-4xl font-bold text-[#0F172A] mb-4"
              style={{ fontFamily: 'Outfit, sans-serif' }}
              data-testid="refrigerant-faq-heading"
            >
              Frequently Asked Questions About HVAC Refrigerants
            </h2>
            <p className="text-base text-slate-500">
              Clear answers to the most common homeowner questions about refrigerant types, phaseouts, and system compatibility.
            </p>
          </div>

          <Accordion type="single" collapsible className="space-y-4">
            {REFRIGERANT_FAQS.map((faq) => (
              <AccordionItem
                key={faq.question}
                value={`rfaq-${faq.question.slice(0, 15)}`}
                data-testid={`refrigerant-faq-item-${faq.question.slice(0, 10).replace(/\s/g, '-')}`}
                className="bg-white rounded-2xl border border-slate-100 px-6 overflow-hidden"
              >
                <AccordionTrigger
                  data-testid={`refrigerant-faq-trigger-${faq.question.slice(0, 10).replace(/\s/g, '-')}`}
                  className="text-left text-[#0F172A] font-semibold hover:no-underline py-5"
                  style={{ fontFamily: 'Outfit, sans-serif' }}
                >
                  {faq.question}
                </AccordionTrigger>
                <AccordionContent
                  data-testid={`refrigerant-faq-content-${faq.question.slice(0, 10).replace(/\s/g, '-')}`}
                  className="text-slate-600 leading-relaxed pb-5"
                >
                  {faq.answer}
                </AccordionContent>
              </AccordionItem>
            ))}
          </Accordion>
        </div>
      </section>

      {/* Important Message */}
      <section className="py-12 bg-[#0F172A]" data-testid="important-message-section">
        <div className="max-w-3xl mx-auto px-6 text-center">
          <p className="text-2xl md:text-3xl font-bold text-white" style={{ fontFamily: 'Outfit, sans-serif' }}>
            "Refrigerant type alone does not determine whether an HVAC system must be replaced."
          </p>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20" data-testid="refrigerant-guide-cta">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-[#0F172A] mb-4" style={{ fontFamily: 'Outfit, sans-serif' }}>
            Ready to Explore Your Options?
          </h2>
          <p className="text-slate-600 text-lg mb-8">
            Use our free tool to get an instant estimate for HVAC replacement based on your home
            and system details.
          </p>
          <a href="/instant-hvac-estimate-new-orleans">
            <Button
              data-testid="cta-estimate-btn"
              className="bg-[#F97316] hover:bg-[#EA580C] text-white font-bold py-4 px-10 rounded-full text-lg orange-glow transition-all duration-300 transform hover:-translate-y-1"
            >
              Get Your Instant HVAC Replacement Estimate
              <ArrowRight className="w-5 h-5 ml-2" />
            </Button>
          </a>
        </div>
      </section>

      {/* Related Articles */}
      <RelatedArticles currentPath="/hvac-refrigerant-guide" />

      <Footer />
    </div>
  );
};

export default RefrigerantGuidePage;
